import { Component, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { CommonModule } from '@angular/common';

import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';

import { debounceTime } from 'rxjs/operators';

import { InputsModule, InputUtilitiesModule, MDBBootstrapModulesPro } from 'ng-uikit-pro-standard';

import { Address, IAddress } from '../../../../models/address.model';

import { User } from '../../../../models/user.model';
import { LegalStatusEnum } from '../../../../models/enums/legal-status.enum';

import { BaryDataSearchService } from '../../../../services/bary-data-search.service';

import { MarkAsteriskDirective } from '../../../../directives/mark-asterisk.directive';
import { TranslateDirective } from '../../../../directives/translate.directive';

import { BaryButtonComponent } from '../bary-button/bary-button.component';

import { BaryComponentHelper } from '../../util/bary-component-helper';

@Component({
  selector: 'jhi-bary-siret',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    CommonModule,
    InputUtilitiesModule,
    InputsModule,
    MDBBootstrapModulesPro,
    MarkAsteriskDirective,
    TranslateDirective,
    NgxSpinnerModule,
    TranslateModule,
    BaryButtonComponent
  ],
  templateUrl: './bary-siret.component.html',
  styleUrls: ['./bary-siret.component.scss'],
})
export class BarySiretComponent implements OnInit {

  readonly #spinnerService = inject(NgxSpinnerService);
  readonly #baryDataSearchService = inject(BaryDataSearchService);
  readonly #translateService = inject(TranslateService);

  @Input() control?: FormControl;
  @Input() id: string = '';
  @Input() disabled: boolean = false;
  @Input() autoSearch: boolean  = false;
  @Input() searchButton: boolean  = true;

  @Output() companyLegalStatus = new EventEmitter<LegalStatusEnum>();
  @Output() siretChange = new EventEmitter<string>();
  @Output() addressChange = new EventEmitter<IAddress>();
  @Output() vatNumberChange = new EventEmitter<string>();
  @Output() companyNameChange = new EventEmitter<string>();
  @Output() errorEvent = new EventEmitter<string>();

  SPINNER = 'siret-spinner';
  siretOk = false;
  siretNotFound = false;
  _innerControl = new FormControl('', [Validators.required, Validators.pattern('^[0-9]{14}')]);
  result = {
    companyName: '',
    zipCode: '',
    city: '',
  };

  ngOnInit(): void {
    if (!this.id) {
      this.id = BaryComponentHelper.makeId();
    }
    if (this.control) {
      this._innerControl = this.control;
    }
    this.applyChange();
  }

  find(): void {
    this.findSiret(this._innerControl.value as string);
  }

  findSiret(siret: string): void {
    this.siretOk = false;
    this.siretNotFound = false;
    if (siret.length === 14) {
      this.#spinnerService.show(this.SPINNER).then();
      this.#baryDataSearchService
        .findBySiret(siret)
        .pipe(debounceTime(300))
        .subscribe(
          user => {
            this.dispatchAddressChange(user);

            this.vatNumberChange.emit(user.vatNumber);
            this.companyNameChange.emit(user.companyName);
            this.result.companyName = user.companyName ?? '';
            this.result.city = user.companyAddressCity!;
            this.result.zipCode = user.companyAddressZipCode!;
            this.#spinnerService.hide(this.SPINNER).then();
            this.siretOk = true;
          },
          () => {
            this.siretOk = false;
            this.siretNotFound = true;
            this.errorEvent.emit(this.#translateService.instant('components.siret.notFound'));
            this.#spinnerService.hide(this.SPINNER).then();
          }
        );
    }
  }

  private applyChange(): void {
    this._innerControl.valueChanges.subscribe(siret => {
      this.siretNotFound = false;
      this.siretOk = false;
      if (this.autoSearch) {
        this.findSiret(siret as string);
      }
    });
  }

  private dispatchAddressChange(user: User): void {
    const companyAddress = new Address({
      fullname: user.companyName,
      streetNumber: user.companyAddressStreetNumber,
      street: user.companyAddressStreet,
      zipCode: user.companyAddressZipCode,
      city: user.companyAddressCity,
      country: user.companyAddressCountry,
      additionals: user.companyAddressAdditionals
    });
    this.addressChange.emit(companyAddress);
  }
}
